import React from 'react';
import {Layout, Menu, Avatar, Dropdown, Button, theme, Drawer, Grid} from 'antd';
import {
    DashboardOutlined,
    TeamOutlined,
    ApartmentOutlined,
    ShopOutlined,
    FileTextOutlined,
    BarChartOutlined,
    LineChartOutlined,
    HistoryOutlined,
    RadarChartOutlined,
    UserOutlined,
    LogoutOutlined,
    CustomerServiceOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    CrownOutlined,
    RobotOutlined,
    SettingOutlined,
    MailOutlined,
    MonitorOutlined,
    DollarOutlined,
    AppstoreOutlined,
    AppstoreAddOutlined,
    StarOutlined,
    GiftOutlined,
    ToolOutlined,
    GlobalOutlined,
    FunnelPlotOutlined,
    ContactsOutlined,
    CloudOutlined,
    SafetyCertificateOutlined,
    NodeIndexOutlined,
} from '@ant-design/icons';
import {Link, Outlet, useLocation} from 'react-router-dom';
import {useAuth} from '../../hooks/useAuth';
import {Utils} from '../../utils/utils';
import {ProtectedAdminRoute} from './ProtectedAdminRoute';
import {HeaderVersionInfo} from '../../components/HeaderVersionInfo';
import {CrmReminderBell} from './crm/CrmReminderBell';
import '../styles/admin-responsive.css';

const {Header, Sider, Content} = Layout;

export const AdminLayout: React.FC = () => {
    const [collapsed, setCollapsed] = React.useState(false);
    const [mobileDrawerVisible, setMobileDrawerVisible] = React.useState(false);
    const {user, logout} = useAuth();
    const location = useLocation();
    const {token} = theme.useToken();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    // Keep submenus open when a child is selected
    const getDefaultOpenKeys = () => {
        const path = location.pathname;
        const open: string[] = [];
        const subscriptionPaths = ['/admin/subscription-plans', '/admin/features', '/admin/addons', '/admin/founding-cohort', '/admin/rewards'];
        if (subscriptionPaths.some(p => path.startsWith(p))) {
            open.push('subscriptions-submenu');
        }
        if (path.startsWith('/admin/response-integrity')) {
            open.push('response-integrity-submenu');
        }
        return open;
    };

    const getSelectedMenuKey = () => {
        const path = location.pathname;
        
        // Check for brands sub-routes (brand equity and statements)
        if (path.startsWith('/admin/brands')) {
            return '/admin/brands';
        }
        
        if (path.startsWith('/admin/ai-experts')) {
            return '/admin/ai-experts';
        }
        
        // Response Integrity sub-routes
        if (path.startsWith('/admin/response-integrity/chargebacks')) {
            return '/admin/response-integrity/chargebacks';
        }
        if (path.startsWith('/admin/response-integrity/detectors')) {
            return '/admin/response-integrity/detectors';
        }
        if (path.startsWith('/admin/response-integrity/profiles')) {
            return '/admin/response-integrity/profiles';
        }
        if (path.startsWith('/admin/response-integrity/settings')) {
            return '/admin/response-integrity/settings';
        }
        if (path.startsWith('/admin/response-integrity')) {
            return '/admin/response-integrity';
        }

        // For other routes, use exact path matching
        return path;
    };

    const adminMenuItems = [
        {
            key: '/admin/dashboard',
            icon: <DashboardOutlined />,
            label: <Link to="/admin/dashboard">Dashboard</Link>,
        },
        {
            key: '/admin/organizations',
            icon: <ApartmentOutlined />,
            label: <Link to="/admin/organizations">Organisations</Link>,
        },
        {
            key: '/admin/agencies',
            icon: <TeamOutlined />,
            label: <Link to="/admin/agencies">Agencies</Link>,
        },
        {
            key: '/admin/signup-funnel',
            icon: <FunnelPlotOutlined />,
            label: <Link to="/admin/signup-funnel">Signup Funnel</Link>,
        },
        {
            key: '/admin/crm',
            icon: <ContactsOutlined />,
            label: <Link to="/admin/crm">CRM</Link>,
        },
        {
            key: '/admin/mail',
            icon: <MailOutlined />,
            label: <Link to="/admin/mail">Mail</Link>,
        },
        {
            key: '/admin/brands',
            icon: <ShopOutlined />,
            label: <Link to="/admin/brands">Brands</Link>,
        },
        {
            key: '/admin/surveys',
            icon: <FileTextOutlined />,
            label: <Link to="/admin/surveys">Surveys</Link>,
        },
        {
            key: '/admin/trends',
            icon: <BarChartOutlined />,
            label: <Link to="/admin/trends">Trends</Link>,
        },
        {
            key: '/admin/benchmarking',
            icon: <LineChartOutlined />,
            label: <Link to="/admin/benchmarking">Benchmarking</Link>,
        },
        {
            key: '/admin/messages',
            icon: <MailOutlined />,
            label: <Link to="/admin/messages">Messages</Link>,
        },
        {
            key: '/admin/response-monitoring',
            icon: <MonitorOutlined />,
            label: <Link to="/admin/response-monitoring">Response Monitoring</Link>,
        },
        {
            key: 'response-integrity-submenu',
            icon: <SafetyCertificateOutlined />,
            label: 'Response Integrity',
            children: [
                {
                    key: '/admin/response-integrity',
                    label: <Link to="/admin/response-integrity">Reviewer Dashboard</Link>,
                },
                {
                    key: '/admin/response-integrity/chargebacks',
                    label: <Link to="/admin/response-integrity/chargebacks">Chargebacks</Link>,
                },
                ...(user && Utils.userIsSuperAdmin(user) ? [
                    {
                        key: '/admin/response-integrity/detectors',
                        label: <Link to="/admin/response-integrity/detectors">Detectors</Link>,
                    },
                    {
                        key: '/admin/response-integrity/profiles',
                        label: <Link to="/admin/response-integrity/profiles">Profiles</Link>,
                    },
                    {
                        key: '/admin/response-integrity/settings',
                        label: <Link to="/admin/response-integrity/settings">Integrity Settings</Link>,
                    },
                ] : []),
            ],
        },
        {
            key: '/admin/live-analytics',
            icon: <RadarChartOutlined />,
            label: <Link to="/admin/live-analytics">Live Analytics</Link>,
        },
        {
            key: '/admin/events',
            icon: <HistoryOutlined />,
            label: <Link to="/admin/events">Event Log</Link>,
        },
        {
            key: '/admin/cloudwatch-logs',
            icon: <CloudOutlined />,
            label: <Link to="/admin/cloudwatch-logs">CloudWatch Logs</Link>,
        },
        {
            key: '/admin/waitlist',
            icon: <TeamOutlined />,
            label: <Link to="/admin/waitlist">Waitlist</Link>,
        },
        {
            key: '/admin/support',
            icon: <CustomerServiceOutlined />,
            label: <Link to="/admin/support">Support</Link>,
        },
        {
            key: '/admin/ai',
            icon: <NodeIndexOutlined />,
            label: <Link to="/admin/ai">AI Administration</Link>,
        },
        {
            key: '/admin/ai-experts/chat',
            icon: <RobotOutlined />,
            label: <Link to="/admin/ai-experts/chat">AI Expert Chat</Link>,
        },
        ...(user && Utils.userIsSuperAdmin(user) ? [
            {
                key: '/admin/users',
                icon: <TeamOutlined />,
                label: <Link to="/admin/users">Platform Users</Link>,
            },
            {
                key: 'subscriptions-submenu',
                icon: <DollarOutlined />,
                label: 'Subscriptions',
                children: [
                    {
                        key: '/admin/subscription-plans',
                        icon: <DollarOutlined />,
                        label: <Link to="/admin/subscription-plans">Plans</Link>,
                    },
                    {
                        key: '/admin/features',
                        icon: <AppstoreOutlined />,
                        label: <Link to="/admin/features">Features</Link>,
                    },
                    {
                        key: '/admin/addons',
                        icon: <AppstoreAddOutlined />,
                        label: <Link to="/admin/addons">Add-ons</Link>,
                    },
                    {
                        key: '/admin/founding-cohort',
                        icon: <StarOutlined />,
                        label: <Link to="/admin/founding-cohort">Founding Cohort</Link>,
                    },
                    {
                        key: '/admin/rewards',
                        icon: <GiftOutlined />,
                        label: <Link to="/admin/rewards">Rewards</Link>,
                    },
                ],
            },
            {
                key: '/admin/statement-templates',
                icon: <FileTextOutlined />,
                label: <Link to="/admin/statement-templates">Statement Templates</Link>,
            },
            {
                key: '/admin/system-settings',
                icon: <SettingOutlined />,
                label: <Link to="/admin/system-settings">System Settings</Link>,
            },
            {
                key: '/admin/legal-documents',
                icon: <FileTextOutlined />,
                label: <Link to="/admin/legal-documents">Legal Documents</Link>,
            },
            {
                key: '/admin/tools',
                icon: <ToolOutlined />,
                label: <Link to="/admin/tools">Tools</Link>,
            },
            {
                key: '/admin/corporate-site',
                icon: <GlobalOutlined />,
                label: <Link to="/admin/corporate-site">Corporate Site</Link>,
            },
        ] : []),
    ];

    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: <Link to="/admin/profile">Profile</Link>,
        },
        {
            type: 'divider' as const,
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: logout,
        },
    ];

    const renderMenu = () => (
        <>
            <div style={{
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                padding: collapsed && !isMobile ? 0 : '0 24px',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: token.colorPrimary,
                }}>
                    <CrownOutlined style={{marginRight: collapsed && !isMobile ? 0 : 8}} />
                    {(!collapsed || isMobile) && 'Admin Panel'}
                </div>
            </div>

            <Menu
                theme="light"
                mode="inline"
                selectedKeys={[getSelectedMenuKey()]}
                defaultOpenKeys={getDefaultOpenKeys()}
                items={adminMenuItems}
                style={{
                    border: 'none',
                    marginTop: 16,
                }}
                onClick={() => {
                    if (isMobile) {
                        setMobileDrawerVisible(false);
                    }
                }}
            />
        </>
    );

    return (
        <ProtectedAdminRoute>
            <Layout style={{minHeight: '100vh'}}>
                {!isMobile && (
                    <Sider
                        trigger={null}
                        collapsible
                        collapsed={collapsed}
                        style={{
                            background: token.colorBgContainer,
                            borderRight: `1px solid ${token.colorBorderSecondary}`,
                        }}
                    >
                        {renderMenu()}
                    </Sider>
                )}

                {isMobile && (
                    <Drawer
                        placement="left"
                        closable={false}
                        onClose={() => setMobileDrawerVisible(false)}
                        open={mobileDrawerVisible}
                        width={240}
                        styles={{
                            body: { padding: 0 },
                            header: { display: 'none' },
                        }}
                    >
                        {renderMenu()}
                    </Drawer>
                )}

            <Layout>
                <Header style={{
                    padding: isMobile ? '0 16px' : '0 24px',
                    background: token.colorBgContainer,
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <Button
                            type="text"
                            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
                            onClick={() => isMobile ? setMobileDrawerVisible(true) : setCollapsed(!collapsed)}
                            style={{
                                fontSize: '16px',
                                width: isMobile ? 48 : 64,
                                height: isMobile ? 48 : 64,
                            }}
                        />
                        {!isMobile && (
                            <span style={{
                                marginLeft: 16,
                                color: token.colorTextSecondary,
                                fontSize: '14px',
                            }}>
                                Platform Administration
                            </span>
                        )}
                    </div>

                    <div style={{display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16}}>
                        {!isMobile && <HeaderVersionInfo />}

                        <CrmReminderBell />

                        <Dropdown
                            menu={{items: userMenuItems}}
                            placement="bottomRight"
                            trigger={['click']}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '8px 12px',
                                borderRadius: token.borderRadius,
                            }}>
                                <Avatar
                                    size="small"
                                    icon={<UserOutlined />}
                                    style={{marginRight: 8}}
                                />
                                {!isMobile && (
                                    <div>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: token.colorText,
                                            lineHeight: 1.2,
                                            marginBottom: 2,
                                        }}>
                                            {user?.firstName && user?.lastName 
                                                ? `${user.firstName} ${user.lastName}`.trim()
                                                : user?.email || 'User'
                                            }
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: token.colorTextSecondary,
                                            lineHeight: 1.2,
                                        }}>
                                            {user && Utils.userIsSuperAdmin(user) ? 'Super Admin' : 'Admin'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Dropdown>
                    </div>
                </Header>

                <Content style={{
                    margin: isMobile ? '16px' : '24px',
                    padding: 0,
                    background: token.colorBgLayout,
                    borderRadius: token.borderRadius,
                }}>
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
        </ProtectedAdminRoute>
    );
};